import React, { useMemo, useState } from "react";
import {
  useGetCurrentUserQuery,
  useUpdateUserProfileMutation,
} from "../../../api/users";
import { InputField } from "../../../components/forms/InputField";
import { Card } from "../../../components/layout/Card";
import {
  ErrorWrapper,
  InputWrapper,
} from "../questionnaireInputs/SharedFormStyles";
import RequiredStar from "../../../components/RequiredAsterisk";
import {
  validateDateOfBirth,
  formatDateForHTMLInput,
} from "../../../utils/dateUtils";
import { CardContainer } from "../Question";

const userBirthDateQuestionConfig = {
  title: "What is your date of birth?",
  isRequired: true,
};

const UserBirthDate = ({
  shouldValidateQuestionnaireSection = false,
}: {
  shouldValidateQuestionnaireSection?: boolean;
}) => {
  const { data: user } = useGetCurrentUserQuery();
  const [
    updateUser,
    { /*isLoading,*/ isError, /*isSuccess: isUpdateSuccess,*/ reset },
  ] = useUpdateUserProfileMutation();

  const [dateError, setDateError] = useState<string | null>(null);

  const dateInput = useMemo(() => {
    return user?.birthdate ? formatDateForHTMLInput(user.birthdate) : "";
  }, [user?.birthdate]);

  const save = (value: string | null) => {
    if (!user?.id) return;

    void updateUser({
      userId: user.id,
      fieldName: "birthdate",
      fieldValue: value ? new Date(value) : null,
      debounceMs: 1000,
    })
      .unwrap()
      .catch(() => {
        // Intentionally swallow: UI is driven by RTK Query `isError` state.
        // Avoid unhandled promise rejections in tests/runtime.
      });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    reset();
    const value = e.target.value;

    const validationError = validateDateOfBirth(value);
    setDateError(validationError);

    if (validationError) {
      save(null);
      return;
    }
    save(value);
  };

  const validationError = !!(!dateInput && shouldValidateQuestionnaireSection);
  //const isSuccess = isUpdateSuccess && !isLoading;

  return (
    <CardContainer>
      <Card
        header={{
          title: (
            <>
              {userBirthDateQuestionConfig.title}
              {userBirthDateQuestionConfig.isRequired && <RequiredStar />}
            </>
          ),
        }}
        isError={validationError}
      >
        <InputWrapper>
          <InputField
            type="date"
            placeholder="mm/dd/yyyy"
            value={dateInput}
            onChange={handleDateChange}
            required={userBirthDateQuestionConfig.isRequired}
            errorMessage={dateError}
          />
          {/* Commenting out the loader and success icon for now as per the request of the design team */}
          {/*
          {isLoading && (
            <IconContainer>
              <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
            </IconContainer>
          )}
          {isSuccess && (
            <IconContainer>
              <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
            </IconContainer>
          )}
          */}
        </InputWrapper>

        {isError && (
          <ErrorWrapper>
            Error saving answer, please refresh the page and try again
          </ErrorWrapper>
        )}
      </Card>
    </CardContainer>
  );
};

export default UserBirthDate;
